import { useEffect } from 'react'
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react'

/**
 * Toast Notification Component
 * بوب أب إشعار جميل
 *
 * Types:
 * - success: نجاح (أخضر)
 * - error: خطأ (أحمر)
 * - warning: تحذير (برتقالي)
 * - info: معلومة (أزرق)
 *
 * Usage:
 * <Toast
 *   type="success"
 *   message="تم الحفظ بنجاح"
 *   onClose={() => setShowToast(false)}
 *   duration={3000}
 * />
 */
const Toast = ({ type = 'info', message, onClose, duration = 3000 }) => {
  // Auto close after duration
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration)
      return () => clearTimeout(timer)
    }
  }, [duration, onClose])

  // Get icon and colors based on type
  const getConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: CheckCircle,
          bgColor: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-500 dark:border-green-400',
          iconColor: 'text-green-600 dark:text-green-400',
          textColor: 'text-green-800 dark:text-green-200'
        }
      case 'error':
        return {
          icon: AlertCircle,
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-500 dark:border-red-400',
          iconColor: 'text-red-600 dark:text-red-400',
          textColor: 'text-red-800 dark:text-red-200'
        }
      case 'warning':
        return {
          icon: AlertTriangle,
          bgColor: 'bg-orange-50 dark:bg-orange-900/20',
          borderColor: 'border-orange-500 dark:border-orange-400',
          iconColor: 'text-orange-600 dark:text-orange-400',
          textColor: 'text-orange-800 dark:text-orange-200'
        }
      case 'info':
      default:
        return {
          icon: Info,
          bgColor: 'bg-blue-50 dark:bg-blue-900/20',
          borderColor: 'border-blue-500 dark:border-blue-400',
          iconColor: 'text-blue-600 dark:text-blue-400',
          textColor: 'text-blue-800 dark:text-blue-200'
        }
    }
  }

  const config = getConfig()
  const Icon = config.icon

  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[999] animate-slide-down">
      <div
        className={`
          flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border-r-4
          ${config.bgColor} ${config.borderColor}
          min-w-[300px] max-w-md
        `}
      >
        <Icon className={`w-5 h-5 flex-shrink-0 ${config.iconColor}`} />
        <p className={`flex-1 text-sm font-medium ${config.textColor}`}>
          {message}
        </p>
        <button
          onClick={onClose}
          className={`p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors ${config.textColor}`}
          aria-label="إغلاق"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

export default Toast
